import React, { useCallback, useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { GetAction } from '../../Component/GetAction'
import { PostAction } from '../../Component/PostAction'
import ManageTable from '../ManageTable'
import { RequireModule, PageHeader, BTN_PRIMARY } from './shared'

const EMPTY = { expense_type_id: '', name: '' }

function ExpenseTypesPage() {
  const [rows, setRows] = useState([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [togglingId, setTogglingId] = useState(null)
  const [form, setForm] = useState(EMPTY)

  const load = useCallback(async () => {
    setLoading(true)
    const data = await GetAction('get', { all: 1 }, 'expense_type/list')
    if (data?.status === 'success' && Array.isArray(data.response)) {
      setRows(data.response)
      setError('')
    } else {
      setError(data?.message || 'Failed to load expense types.')
      setRows([])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const closeForm = () => setShowForm(false)

  const openNew = () => {
    setForm(EMPTY)
    setShowForm(true)
    setMessage(null)
  }

  const openEdit = (row) => {
    setForm({
      expense_type_id: row.expense_type_id || '',
      name: row.name || '',
    })
    setShowForm(true)
    setMessage(null)
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setMessage({ error: true, text: 'Category name is required.' })
      return
    }
    setSaving(true)
    setMessage(null)
    const payload = {
      expense_type_id: form.expense_type_id || undefined,
      name: form.name.trim(),
    }
    const data = await PostAction('post', payload, 'expense_type/save')
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Saved.' })
      setShowForm(false)
      await load()
    } else {
      setMessage({ error: true, text: data?.message || 'Failed to save.' })
    }
    setSaving(false)
  }

  const toggleStatus = async (row) => {
    const next = Number(row.status) === 1 ? 0 : 1
    setTogglingId(row.expense_type_id)
    setMessage(null)
    const data = await PostAction(
      'post',
      { expense_type_id: row.expense_type_id, status: next },
      'expense_type/toggle'
    )
    setTogglingId(null)
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Updated.' })
      await load()
    } else {
      setMessage({ error: true, text: data?.message || 'Failed to update status.' })
    }
  }

  const columns = [
    { key: 'index', label: '#', width: '60px' },
    { key: 'name', label: 'Category' },
    {
      key: 'status',
      label: 'Status',
      render: (row) =>
        Number(row.status) === 1 ? (
          <span className="manage-menu-badge manage-menu-badge-dd">Enabled</span>
        ) : (
          <span className="manage-menu-badge">Disabled</span>
        ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="d-flex flex-wrap gap-1">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => openEdit(row)}
          >
            Edit
          </button>
          <button
            type="button"
            className={`btn btn-sm ${
              Number(row.status) === 1 ? 'btn-outline-secondary' : 'btn-outline-success'
            }`}
            disabled={togglingId === row.expense_type_id}
            onClick={() => toggleStatus(row)}
          >
            {Number(row.status) === 1 ? 'Disable' : 'Enable'}
          </button>
        </div>
      ),
    },
  ]

  return (
    <RequireModule moduleKey="expense_types">
      <PageHeader
        title="Expense Types"
        subtitle="Expense categories. Categories cannot be deleted — disable them instead."
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            style={BTN_PRIMARY}
            onClick={openNew}
          >
            Add Category
          </button>
        }
      />
      {message ? (
        <div
          className={`manage-alert ${message.error ? 'manage-alert-err' : 'manage-alert-ok'}`}
        >
          {message.text}
        </div>
      ) : null}
      {error ? <div className="manage-alert manage-alert-err">{error}</div> : null}

      <div className="manage-card mb-4">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : (
          <ManageTable columns={columns} rows={rows} searchKeys={['name']} />
        )}
      </div>

      <Modal show={showForm} onHide={closeForm} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {form.expense_type_id ? 'Edit Expense Type' : 'Add Expense Type'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form id="manage-expense-type-form" onSubmit={onSubmit}>
            <label className="form-label" htmlFor="et_name">
              Category name
            </label>
            <input
              id="et_name"
              className="form-control"
              required
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={closeForm}>
            Cancel
          </button>
          <button
            type="submit"
            form="manage-expense-type-form"
            className="btn btn-primary"
            style={BTN_PRIMARY}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </Modal.Footer>
      </Modal>
    </RequireModule>
  )
}

export default ExpenseTypesPage
